import { Collection, LongString, MaxString, ModelBase, NormalString, ObservableCollection, Property, Required, VeryLongString } from '@skylab-shared/core';
import { DdkState } from '../accountancy/ddk-state';
import { JpkVatState } from '../accountancy/jpk-vat-state';
import { AddressModel } from '../address/address.model';
import { CalculationMethod } from '../common/calculation-method';
import { EppState } from '../common/epp-state';
import { RelatedEntity } from '../common/related-entity';
import { VatReleaseReasonSource } from '../company/vat-release-reason-source';
import { AdvanceSummary } from '../document/advance-summary';
import { DocumentBuyerModel } from '../document/document-buyer.model';
import { DocumentClientModel } from '../document/document-client.model';
import { DocumentPaymentListItemModel } from '../document/document-payment-list-item.model';
import { VatSummary } from '../document/vat-summary';
import { ExchangeRateModel } from '../exchange-rate/exchange-rate.model';
import { FiscalizationState } from '../fiscal/fiscal-state';
import { InventoryEffect } from '../inventory/inventory-effect';
import { KsefDocumentStatus } from '../ksef/ksef-document-status';
import { KsefProcessingStage } from '../ksef/ksef-processing-stage';
import { KsefSendSubmissionMode } from '../ksef/ksef-send-submission-mode';
import { KsefSessionState } from '../ksef/ksef-session-state';
import { VatTableItemModel } from '../vat-table/vat-table-item.model';
import { AdvanceState } from './advance-state';
import { RateNP } from './rate-np';
import { SaleAdvanceDocumentItemModel } from './sale-advance-invoice-item.model';
import { SaleDocumentKind } from './sale-document-kind';
import { OrderState } from './sale-order-state';
import { SplitPaymentState } from './split-payment-state';

export class SaleAdvanceDocumentModel extends ModelBase {
    @Property()
    public advanceValue: number;

    @Property()
    public advanceState: AdvanceState;

    @Property({ nullable: true })
    public advanceId?: number;

    @Property({ nullable: true })
    public firstAdvanceDocumentId?: number;  // dla pierwszej zaliczki nie wysyłać

    @Property()
    public firstAdvanceDocumentVersion: string; // dla pierwszej zaliczki nie wysyłać

    @Collection({ type: SaleAdvanceDocumentItemModel })
    public items: ObservableCollection<SaleAdvanceDocumentItemModel>;

    @Property({ identifiable: true })
    public id: number;

    @Property(Number, { nullable: true })
    public bankAccountNumberId?: number | null;

    @Property(Number, { nullable: true })
    public baseCurrencyBankAccountNumberId?: number | null;

    @Property(DocumentBuyerModel, { nullable: true })
    public buyer: DocumentBuyerModel | null;

    @Property()
    @Required()
    public calculationMethod: CalculationMethod;

    @Property()
    public cashBasis: boolean;

    @Property(DocumentClientModel, { nullable: true })
    public client: DocumentClientModel | null;

    @Property()
    @Required()
    public communityTransactionId: number;

    @Property(String, { nullable: true })
    public rateNP: RateNP | null;

    @Property(Boolean, { nullable: true })
    public reverseCharge: boolean | null;

    @Property()
    @Required()
    public currencyId: number;

    @Property()
    public ddkState: DdkState;

    @Property()
    public deliveryAddress: AddressModel;

    @Property()
    @VeryLongString('data-model.document.validation.document-number.length')
    public documentNumber: string;

    @Property()
    public dueDate: Date;

    @Property()
    public dueDateInDays: number;

    @Property()
    public eppState: EppState;

    @Property()
    public exchangeRate: ExchangeRateModel;

    @Property()
    public favourite: boolean;

    @Property()
    public fiscalizationState: FiscalizationState;

    @Property()
    public grossValue: number;

    @Property({ nullable: true })
    public implementedDocumentId: number;

    @Property({ nullable: true })
    public implementedDocumentVersion: string;

    @Property({ nullable: true })
    public invoiceToReceipt?: boolean;

    @Property()
    public issueDate: Date;

    @Property(Date, { nullable: true })
    public goodsIssueDate: Date | null;

    @Property()
    public issuedByVatPayer: boolean;

    @Property()
    public issuer: string;

    @Property()
    public jpk_VATState: JpkVatState;

    @Property()
    public jpkVatGroups: string[];

    @Property()
    public readonly kind: SaleDocumentKind = SaleDocumentKind.AdvanceInvoice;

    @Property({ nullable: true })
    public marginSchemeId?: number;

    @Property()
    public netValue: number;

    @Property()
    @MaxString('data-model.document.validation.note.length')
    public note: string;

    @Property()
    public numberingId: number;

    @Property()
    public orderState: OrderState;

    @Collection({ type: DocumentPaymentListItemModel })
    public payments: ObservableCollection<DocumentPaymentListItemModel>;

    @Property()
    @Required()
    public paymentMethodId: number;

    @Property()
    @NormalString('data-model.document.validation.recipient.length')
    public recipient: string;

    @Property({ nullable: true })
    public saleDate?: Date;

    @Property()
    public splitPayment: SplitPaymentState;

    @Property({ nullable: true })
    public taxLiabilityDate: Date;

    @Property()
    @Required('data-model.document.validation.transaction-location.non-empty')
    @LongString('data-model.document.validation.transaction-location.length')
    public transactionLocation: string;

    @Collection({ type: VatTableItemModel })
    public vatTableItems: ObservableCollection<VatTableItemModel>;

    @Collection({ type: VatTableItemModel })
    public vatTableItemsInBaseCurrency: ObservableCollection<VatTableItemModel>;

    public vatSummary: VatSummary | AdvanceSummary;

    @Property({ nullable: true })
    public vatReleaseReasonId?: number;

    @Property({ nullable: true })
    public vatReleaseReasonCustomName?: string;

    @Property({ nullable: true })
    public vatReleaseReasonSource?: VatReleaseReasonSource;

    @Property({ nullable: true })
    public wstoShipmentCountryId: number;

    @Property(Number, { nullable: true })
    public wstoConsumerCountryId: number | null;

    @Property({ nullable: true })
    @MaxString('data-model.document.validation.wsto-additional-information.length')
    public wstoAdditionalInformation: string;

    @Property(String, { nullable: true })
    @MaxString('data-model.document.validation.custom-due-date-description.length')
    public customDueDateDescription: string | null;

    @Property({ nullable: false })
    public ksefSubmissionMode: KsefSendSubmissionMode;

    @Property({ nullable: true })
    public ksefProcessingStage: KsefProcessingStage;

    @Property({ nullable: true })
    public ksefDocumentStatus: KsefDocumentStatus;

    @Property({ nullable: true })
    public ksefSessionState: KsefSessionState;

    @Property({ nullable: true })
    public ksefDocumentId: string;

    @Property(String, { nullable: true })
    public ksefCollectiveIdentifier: string | null;

    @Property({ nullable: true })
    public ksefAcquisitionDateTime: Date;

    @Property({ nullable: true })
    public ksefIssueDate: Date;

    @Property({ nullable: true })
    public ksefLastStatusCheckDate: Date;

    @Property({ nullable: true })
    public ksefLastReceivingUpoDate: Date;

    @Property({ nullable: true })
    public ksefInitializationDate: Date;

    @Property({ nullable: true })
    public ksefSessionEndDate: Date;

    @Property({ nullable: true })
    public ksefSessionReferenceNumber: string;

    @Property({ nullable: true })
    public ksefAllDocumentsProcessed: boolean;

    @Property({ nullable: true })
    public ksefHash: string;

    @Property({ nullable: true })
    public ksefUpoDownloadUrlExpirationDate: Date;

    @Property({ nullable: true })
    public ksefUpoDownloadUrl: string;

    @Property({ nullable: true })
    public ksefCertificateUrl: string;

    @Property(Boolean, { nullable: true })
    public ksefXmlExported: boolean | null;

    @Property(Boolean, { nullable: true })
    public ksefXmlImported: boolean | null;

    @Property(String, { nullable: true })
    public ksefXmlUrl: string | null;

    @Property()
    public relatedInventoryDocuments: RelatedEntity[];

    @Property()
    public includesInventoryControl: boolean;

    @Property({nullable: true})
    public inventoryEffect: InventoryEffect;

    @Property({ nullable: true })
    @LongString('data-model.document.validation.order-number.length')
    public orderNumber: string;

    @Property(Date, { nullable: true })
    public orderDate: Date | null;

    @Property({ nullable: true })
    @LongString('data-model.document.validation.agreement-number.length')
    public agreementNumber: string;

    @Property(Date, { nullable: true })
    public agreementDate: Date | null;
}
